import type { GameState } from './types.js';

/**
 * A string that identifies a position for repetition purposes: side to move, terrain, every
 * piece on the board, and whether each piece is currently pinned or immune.
 */
export function positionKey(state: Pick<GameState, 'turn' | 'heights' | 'pieces' | 'ply'>): string {
  const terrain = state.heights.map((row) => row.join('')).join('');
  const pieces = state.pieces
    .filter((p) => p.alive)
    .map((p) => {
      const since = p.pinnedAt === undefined ? -1 : state.ply - p.pinnedAt;
      const pin = since === 1 ? 'p' : since === 2 ? 'i' : '';
      return `${p.id}@${p.r}${p.c}${pin}`;
    })
    .sort()
    .join(',');
  return `${state.turn}|${terrain}|${pieces}`;
}

/** 32-bit FNV-1a hash as 8 hex digits. */
export function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Short fingerprint of the whole game state, used by client and server to detect desyncs and by
 * golden fixtures to lock behaviour across versions.
 */
export function stateHash(state: GameState): string {
  return fnv1a(
    `${state.ply}|${state.winner ?? '-'}|${state.winReason ?? '-'}|${positionKey(state)}`,
  );
}
